/**
 * Schemas for the `cases.*` tRPC procedures.
 *
 * Every count carries a `SourceCitation` envelope (provenance contract). CFR
 * is computed server-side from `deaths / confirmed * 100`, rounded to 2dp.
 * `cases.getTimeSeries` returns the raw rows from `case_counts` joined with
 * provenance — chart components on the web page render from these.
 */

import { z } from "zod";

import { sourceCitationSchema } from "./citations.js";

export const CASE_COUNT_KINDS = [
  "death",
  "active",
  "confirmed",
  "suspected",
  "recovered",
] as const;
export const caseCountKindSchema = z.enum(CASE_COUNT_KINDS);
export type CaseCountKind = z.infer<typeof caseCountKindSchema>;

const counted = z
  .object({ count: z.number().int().min(0) })
  .merge(sourceCitationSchema);

/**
 * Aggregated counts payload. Each numeric field is paired with its source
 * citation. `cfrPercent` is computed (deaths / confirmed * 100) and tagged
 * with the citation of the deaths row, since that is the definitional driver.
 *
 * The three `*SevenDaysAgo` fields are optional plain numbers — they
 * back the dashboard's "↑ +N vs 7 days ago" badge. They're NOT cited
 * envelopes because the badge only shows the delta arithmetic; the
 * underlying citation belongs to the latest count row (which the user
 * clicks through to verify).
 */
export const outbreakCountsSchema = z.object({
  confirmed: counted,
  deaths: counted,
  suspected: counted,
  active: counted,
  recovered: counted,
  cfrPercent: z
    .object({ value: z.number().min(0).max(100) })
    .merge(sourceCitationSchema),
  confirmedSevenDaysAgo: z.number().int().min(0).optional(),
  deathsSevenDaysAgo: z.number().int().min(0).optional(),
  cfrPercentSevenDaysAgo: z.number().min(0).max(100).optional(),
});
export type OutbreakCounts = z.infer<typeof outbreakCountsSchema>;

export const getCountsInputSchema = z.object({
  slug: z.string().min(1).max(100),
});
export type GetCountsInput = z.infer<typeof getCountsInputSchema>;

export const getTimeSeriesInputSchema = z.object({
  slug: z.string().min(1).max(100),
  kind: caseCountKindSchema,
  /** ISO-8601 lower bound (inclusive). Omit for full history. */
  since: z.coerce.date().optional(),
});
export type GetTimeSeriesInput = z.infer<typeof getTimeSeriesInputSchema>;

export const timeSeriesPointSchema = z.object({
  recordedAt: z.date(),
  count: z.number().int().min(0),
  sourceUrl: z.string().url(),
  sourceLabel: z.string().min(1),
});
export type TimeSeriesPoint = z.infer<typeof timeSeriesPointSchema>;

export const getTimeSeriesOutputSchema = z.array(timeSeriesPointSchema);
